import { useState, useEffect, useMemo, useCallback, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { BiSearch } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import AlertService from "../../../../services/AlertService";
import AuthContext from "../../../../auth/AuthContext";
import AssessmentMasterService from "../../../../services/AssessmentMasterService";

const AssessmentMapping = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const authContext = useContext(AuthContext);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);
  const [assignedNewUsers, setAssignedNewUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [initialAssignments, setInitialAssignments] = useState([]);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [hasViewPermission, setHasViewPermission] = useState(false);
  const [assessmentName, setAssessmentName] = useState("");

  const assessmentAccess = useMemo(
    () => authContext.getPermissions("assessmentsmaster"),
    [authContext],
  );
  const showAdd = useMemo(
    () => assessmentAccess.some((f) => f.access.includes(2)),
    [assessmentAccess],
  );
  const showEdit = useMemo(
    () => assessmentAccess.some((f) => f.access.includes(3)),
    [assessmentAccess],
  );

  useEffect(() => {
    const hasPermission = assessmentAccess.some((f) => f.access.includes(1));
    setHasViewPermission(hasPermission);
    if (!hasPermission || !assessmentId) {
      navigate("/");
    }
  }, [assessmentAccess, navigate, assessmentId]);

  const loadAssessmentUsers = useCallback(async () => {
    if (!assessmentId || !hasViewPermission) return;

    setIsInitialLoading(true);
    try {
      const data = await AssessmentMasterService.GetCourseUser(
        assessmentId,
        authContext,
      );
      if (data.isSuccess) {
        setAssessmentName(
          data.data?.assessmentName || `Assessment ${assessmentId}`,
        );

        const processedMapped =
          data.data?.mappedUsers?.map((user) => ({
            ...user,
            selected: false,
          })) || [];

        const processedAll =
          data.data?.allUsers?.map((user) => ({
            ...user,
            selected: false,
          })) || [];

        setAssignedUsers(processedMapped);
        setInitialAssignments(processedMapped);

        if (processedMapped.length > 0) {
          setUsers(
            processedAll.filter(
              (f) => !processedMapped.some((d) => d.value === f.value),
            ),
          );
        } else {
          setUsers(processedAll);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      AlertService.error("Failed to load users for this assessment");
    } finally {
      setIsInitialLoading(false);
    }
  }, [assessmentId, authContext, hasViewPermission]);

  useEffect(() => {
    if (hasViewPermission && assessmentId) {
      loadAssessmentUsers();
    }
  }, [hasViewPermission, assessmentId, loadAssessmentUsers]);

  useEffect(() => {
    const currentValues = assignedUsers.map((u) => u.value);
    const newlyAssigned = assignedUsers.filter(
      (user) => !initialAssignments.some((init) => init.value === user.value),
    );
    const removed = initialAssignments.filter(
      (init) => !currentValues.includes(init.value),
    );

    setAssignedNewUsers(newlyAssigned);
    setRemovedUsers(removed);
  }, [assignedUsers, initialAssignments]);

  const hasChanges = useMemo(() => {
    return assignedNewUsers.length > 0 || removedUsers.length > 0;
  }, [assignedNewUsers, removedUsers]);

  const filteredLeftUsers = useMemo(() => {
    return users.filter((user) =>
      user.label.toLowerCase().includes(searchLeft.toLowerCase()),
    );
  }, [users, searchLeft]);

  const filteredRightUsers = useMemo(() => {
    return assignedUsers.filter((user) =>
      user.label.toLowerCase().includes(searchRight.toLowerCase()),
    );
  }, [assignedUsers, searchRight]);

  useEffect(() => {
    setSelectAllLeft(
      filteredLeftUsers.length > 0 &&
        filteredLeftUsers.every((i) => i.selected),
    );
  }, [filteredLeftUsers]);

  useEffect(() => {
    setSelectAllRight(
      filteredRightUsers.length > 0 &&
        filteredRightUsers.every((i) => i.selected),
    );
  }, [filteredRightUsers]);

  const toggleUserSelection = useCallback((value, isRightPanel = false) => {
    const setter = isRightPanel ? setAssignedUsers : setUsers;
    setter((prev) =>
      prev.map((user) =>
        user.value === value ? { ...user, selected: !user.selected } : user,
      ),
    );
  }, []);

  const toggleSelectAll = useCallback(
    (isRightPanel = false) => {
      const filtered = isRightPanel ? filteredRightUsers : filteredLeftUsers;
      const allSelected = isRightPanel ? selectAllRight : selectAllLeft;
      const valuesToUpdate = filtered.map((i) => i.value);

      const setter = isRightPanel ? setAssignedUsers : setUsers;
      setter((prev) =>
        prev.map((user) =>
          valuesToUpdate.includes(user.value)
            ? { ...user, selected: !allSelected }
            : user,
        ),
      );
    },
    [filteredLeftUsers, filteredRightUsers, selectAllLeft, selectAllRight],
  );

  const moveUsers = useCallback(
    (direction) => {
      if (direction === "right") {
        const selectedUsers = users.filter((i) => i.selected);
        setAssignedUsers((prev) => [
          ...prev,
          ...selectedUsers.map((i) => ({ ...i, selected: false })),
        ]);
        setUsers((prev) => prev.filter((i) => !i.selected));
      } else {
        const selectedUsers = assignedUsers.filter((i) => i.selected);
        setUsers((prev) => [
          ...prev,
          ...selectedUsers.map((i) => ({ ...i, selected: false })),
        ]);
        setAssignedUsers((prev) => prev.filter((i) => !i.selected));
      }
    },
    [users, assignedUsers],
  );

  const handlePreviewClick = (e) => {
    e.preventDefault();
    if (!hasChanges) {
      AlertService.warning("No changes to preview");
      return;
    }
    setShowChangesModal(true);
  };

  const confirmSubmit = useCallback(async () => {
    if (!assessmentId) return;

    setIsSaving(true);
    setShowChangesModal(false);

    try {
      const payload = {
        assessmentId: assessmentId,
        mappedUsers: assignedUsers.map((user) => ({
          value: Number(user.value),
        })),
      };

      const data = await AssessmentMasterService.SaveUsers(
        payload,
        authContext,
      );
      if (data.isSuccess) {
        AlertService.success("User assignments updated successfully");
        loadAssessmentUsers();
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      AlertService.error("Failed to save user assignments");
    } finally {
      setIsSaving(false);
    }
  }, [assessmentId, assignedUsers, authContext, loadAssessmentUsers]);

  const handleCancel = () => {
    navigate(-1);
  };

  const renderSkeletonRows = useCallback((count) => {
    return Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom"
      >
        <div className="me-2">
          <Skeleton shape="circle" size="20px" />
        </div>
        <Skeleton width="70%" height="1.5rem" />
      </div>
    ));
  }, []);

  const modalFooter = (
    <div className="pt-2">
      <button
        className="btn btn-outline-secondary px-5 py-2 me-4"
        onClick={() => setShowChangesModal(false)}
      >
        Back to Edit
      </button>
      <button
        className="btn btn-primary px-5 py-2"
        onClick={async () => {
          await confirmSubmit();
          setShowChangesModal(false);
        }}
      >
        Submit
      </button>
    </div>
  );

  return (
    <div className="container-fluid custom-container mb-3">
      <div className="row g-1 mt-4">
        {/* Left Panel - Course Users */}
        <div
          className="col-12 col-md border rounded p-0 card"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllLeft}
                  onChange={() => toggleSelectAll(false)}
                  disabled={filteredLeftUsers.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? <Skeleton width="120px" /> : "Course Users"}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchLeft}
                    onChange={(e) => setSearchLeft(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>

          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "675px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredLeftUsers.length > 0 ? (
              filteredLeftUsers.map((user) => (
                <div
                  key={user.value}
                  className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom"
                  style={{ minHeight: "56px" }}
                >
                  <Checkbox
                    checked={user.selected}
                    onChange={() => toggleUserSelection(user.value, false)}
                    className="me-3 flex-shrink-0"
                  />
                  <span
                    className="text-break hyphens-auto flex-grow-1"
                    style={{
                      wordBreak: "break-word",
                      lineHeight: "1.4",
                      overflowWrap: "break-word",
                    }}
                  >
                    {user.label}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center p-5 text-muted">
                No users available
              </div>
            )}
          </div>
        </div>

        {/* Middle buttons - arrows*/}
        <div className="col-12 col-md-2 col-lg-1 d-flex flex-md-column justify-content-center align-items-center gap-3">
          {showAdd && (
            <Button
              icon="pi pi-arrow-right"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveUsers("right")}
              disabled={
                isInitialLoading || isSaving || !users.some((i) => i.selected)
              }
            />
          )}
          {showEdit && (
            <Button
              icon="pi pi-arrow-left"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveUsers("left")}
              disabled={
                isInitialLoading ||
                isSaving ||
                !assignedUsers.some((i) => i.selected)
              }
            />
          )}
        </div>

        {/* Right Panel - Assigned Users */}
        <div
          className="col-12 col-md border rounded card p-0"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllRight}
                  onChange={() => toggleSelectAll(true)}
                  disabled={filteredRightUsers.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? (
                  <Skeleton width="130px" />
                ) : (
                  "Assigned Users"
                )}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchRight}
                    onChange={(e) => setSearchRight(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>

          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "675px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredRightUsers.length > 0 ? (
              filteredRightUsers.map((user) => (
                <div
                  key={user.value}
                  className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom"
                  style={{ minHeight: "56px" }}
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    <Checkbox
                      checked={user.selected}
                      onChange={() => toggleUserSelection(user.value, true)}
                      className="me-3 flex-shrink-0"
                    />
                    <span
                      className="text-break hyphens-auto"
                      style={{ wordBreak: "break-word", lineHeight: "1.4" }}
                    >
                      {user.label}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-5 text-muted">
                No users assigned
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end gap-4">
          <Button
            label="Cancel"
            className="btn btn-outline px-5"
            onClick={handleCancel}
          />
          <Button
            className="btn btn-primary px-5 py-2"
            onClick={handlePreviewClick}
            disabled={isInitialLoading || isSaving || !hasChanges}
          >
            Preview
          </Button>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog
        header={<div className="w-100 text-center text-dark">Preview</div>}
        visible={showChangesModal}
        style={{ width: "40vw", height: "85vh" }}
        footer={modalFooter}
        onHide={() => setShowChangesModal(false)}
      >
        <div
          className="bg-white pb-3"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            borderBottom: "1px solid #eee",
          }}
        >
          <h5 className="mb-0 text-dark">
            <b>Assessment</b>: {assessmentName || "Current Assessment"}
          </h5>
        </div>

        <div style={{ paddingTop: "1rem" }}>
          {assignedNewUsers.length > 0 && (
            <div className="mb-3">
              <h6 className="text-dark">Added Users</h6>
              <ul className="list-group">
                {assignedNewUsers.map((u) => (
                  <li key={u.value} className="list-group-item">
                    {u.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removedUsers.length > 0 && (
            <div className="mb-3">
              <h6 className="text-dark">Removed Users</h6>
              <ul className="list-group">
                {removedUsers.map((u) => (
                  <li key={u.value} className="list-group-item">
                    {u.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default AssessmentMapping;
